import { ReactElement, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  MenuCartItem,
  MerchandisingGroup as MerchandisingGroupType,
} from '../../models/menu.types';
import styles from './MenuPage.module.scss';
import ProductCards from './ProductCards';

type Props = {
  index: number
  group: MerchandisingGroupType;
  menuItems: MenuCartItem[];
  isSkipped: boolean;
  animateTiles: boolean;
  isActive: boolean;
  isLoggedIn?: boolean;
  onAddToCart: (menuItem: MenuCartItem) => void;
  onUpdateCartQuantity: (cartItem: MenuCartItem, quantity: number) => void;
  handleSoldOutLtoClick: () => void;
  'data-heap-merch-group-title'?: string;
  isUserInitiatedFilter: boolean;
  isUserInitiatedSearch: boolean;
};

export function MerchandisingGroup({
  index,
  group,
  menuItems,
  isSkipped,
  animateTiles,
  isActive,
  isLoggedIn,
  onAddToCart,
  onUpdateCartQuantity,
  handleSoldOutLtoClick,
  'data-heap-merch-group-title': merchGroupTitle,
  isUserInitiatedFilter,
  isUserInitiatedSearch,
}: Props): ReactElement | null {
  const mgmiByMenuItemId = new Map(
    group.merchandisingGroupMenuItems.map((mgmi) => [mgmi.menuItemId, mgmi.displayOrder]),
  );
  const groupItems = menuItems
    .filter((mi) => mgmiByMenuItemId.has(mi.id))
    .sort((a, b) => (mgmiByMenuItemId.get(a.id) ?? 0) - (mgmiByMenuItemId.get(b.id) ?? 0));

  const location = useLocation();

  const [node, setNode] = useState<HTMLDivElement | null>(null);

  const [searchParams] = useSearchParams();
  const header = searchParams.get('header') || '';
  const search = searchParams.get('search') || '';

  let groupTitle = merchGroupTitle;
  const hashTitle = location.hash.substring(1).replaceAll('%20', ' ').trim();

  const allowFirstViewedMerchGroupHeader = index === 0 && header !== '' && location.hash === '' && (!isUserInitiatedSearch && !isUserInitiatedFilter);
  const hasMerchGroupParamAndHeader = location.hash && hashTitle === merchGroupTitle && header !== '' && (!isUserInitiatedSearch && !isUserInitiatedFilter);

  if (allowFirstViewedMerchGroupHeader === true || hasMerchGroupParamAndHeader) {
    groupTitle = header;
  }

  function getStickyHeaderOffset(): number {
    const wrapper = document.querySelector<HTMLElement>(`.${styles.filter_header_wrapper}`);
    if (!wrapper) return 0;

    const container = document.querySelector<HTMLElement>(`.${styles.filter_header_container}`);
    const top = parseFloat(getComputedStyle(wrapper).top) || 0;
    const { height } = (container ?? wrapper).getBoundingClientRect();

    return top + height;
  }

  useEffect(() => {
    if (!location.hash || hashTitle !== merchGroupTitle || search !== '') return;
    if (!node) return;

    const scrollToElement = () => {
      const offset = getStickyHeaderOffset();
      const elementPosition = node.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth',
      });
    };

    scrollToElement();
  }, [location.hash, hashTitle, merchGroupTitle, node, search, searchParams.toString()]);

  if (!groupItems.length) return null;

  return (
    <div
      key={group.id}
      ref={setNode}
      className={styles.merch_group_container}
      style={{ backgroundColor: group.backgroundColor }}
      id={merchGroupTitle}
    >
      <ProductCards
        menuItemArr={groupItems}
        isSkipped={isSkipped}
        leftAligned
        header={<div className={styles.merch_group_separator_text}>{groupTitle}</div>}
        animateTiles={animateTiles}
        isActive={isActive}
        isLoggedIn={isLoggedIn}
        onAddToCart={onAddToCart}
        onUpdateCartQuantity={onUpdateCartQuantity}
        handleSoldOutLtoClick={handleSoldOutLtoClick}
        data-heap-merch-group-title={merchGroupTitle}
      />
    </div>
  );
}
